import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "./Dashboard";
import { toast } from "sonner";

const STATUSES = [
  { v: "pending", l: "Pendente" },
  { v: "paid", l: "Pago" },
  { v: "preparing", l: "Preparando" },
  { v: "shipped", l: "Enviado" },
  { v: "delivered", l: "Entregue" },
  { v: "cancelled", l: "Cancelado" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data || []));
  useEffect(() => { load(); }, []);

  const update = async (o, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/status`, { status });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
      if (open?.id === o.id) setOpen(data);
      toast.success("Status atualizado");
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  return (
    <div>
      <div className="overline-label">Vendas</div>
      <h1 className="font-display text-4xl text-white tracking-tighter mt-3 mb-8">Pedidos</h1>

      <div className="border border-white/10 bg-[#0A0A0C] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Código</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Pgto</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setOpen(o)} data-testid={`order-row-${o.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.code}</td>
                <td className="px-4 py-3">{o.customer?.name}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(o.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-3">{o.payment_method === "pix" ? "Pix" : "Cartão"}</td>
                <td className="px-4 py-3 text-right font-display">{formatBRL(o.total)}</td>
                <td className="px-4 py-3"><StatusPill s={o.status} /></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="6" className="px-4 py-16 text-center text-zinc-500">Nenhum pedido ainda.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0A0A0C] border-white/10 text-white max-w-2xl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Pedido {open.code}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <div className="overline-label mb-2">Status</div>
                  <Select value={open.status} onValueChange={(v) => update(open, v)}>
                    <SelectTrigger className="bg-[#050505] border-white/15 h-11 text-white max-w-xs" data-testid={`status-select-${open.code}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0C] border-white/15 text-white">
                      {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-5 text-sm">
                  <div>
                    <div className="overline-label mb-2">Cliente</div>
                    <p>{open.customer.name}</p>
                    <p className="text-zinc-400">{open.customer.email}</p>
                    <p className="text-zinc-400">{open.customer.whatsapp}</p>
                    <p className="text-zinc-400">CPF {open.customer.cpf}</p>
                  </div>
                  <div>
                    <div className="overline-label mb-2">Endereço</div>
                    <p className="text-zinc-300 leading-relaxed">
                      {open.address.street}, {open.address.number} {open.address.complement && `· ${open.address.complement}`}<br />
                      {open.address.neighborhood} — {open.address.city}/{open.address.state}<br />
                      CEP {open.address.cep}
                    </p>
                  </div>
                </div>
                <div>
                  <div className="overline-label mb-2">Itens</div>
                  <div className="divide-y divide-white/5 text-sm">
                    {open.items.map((it, i) => (
                      <div key={i} className="flex justify-between py-2">
                        <span>{it.name} × {it.quantity} <span className="text-zinc-500 text-xs">{[it.size && `T ${it.size}`, it.color && `C ${it.color}`].filter(Boolean).join(" · ")}</span></span>
                        <span className="font-display">{formatBRL(it.line_total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 mt-3 border-t border-white/10 text-sm">
                    <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span>{formatBRL(open.subtotal)}</span></div>
                    <div className="flex justify-between text-zinc-300"><span>Frete</span><span>{open.shipping === 0 ? "Grátis" : formatBRL(open.shipping)}</span></div>
                    <div className="flex justify-between text-white pt-2 border-t border-white/10 mt-2"><span>Total</span><span className="font-display text-lg">{formatBRL(open.total)}</span></div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
